/**
 * The owner-ruled retrospective-price warning, shown before any hire date
 * change that recomputes `order_items.line_total` — extend, shorten and
 * change-start. Off-hire and re-hire never touch the planned dates and must
 * never call anything in this file.
 *
 * Owner, 2026-08-18: "popup warning saying this will retrospectivly update
 * the order detials, price, dates etc even for singed off orders, delived,
 * off hired etc."
 *
 * 🔑 ALWAYS SHOWN, on all three actions, every time — the price changes on
 * every edit, not only on a closed order, so the gate must not train anyone
 * to click through it by only firing occasionally. The wording SHARPENS
 * (title + message both change) when the order is already approved,
 * ordered, (partially) delivered, or the item itself is off-hired — see
 * `closedHireStatusLabel`. This is the one definition both
 * `HireActionModal.js` (extend, via its date-picker form) and
 * `GanttView.js` (all three, via drag) render through — never re-type the
 * wording at a call site.
 */

import {
  hireLineCost,
  hireDaysBetween,
  calendarDayGap,
  hireWeekBoundary,
  hireLineQuantity,
  normaliseHireRateType,
  hireRateDisplay,
  parseHireDate,
  MS_PER_DAY,
} from './hireCost';
import { formatDate } from './dateFormat';

const CLOSED_ORDER_STATUS_LABELS = {
  approved: 'approved',
  ordered: 'ordered',
  partially_delivered: 'partially delivered',
  delivered: 'delivered',
};

const HIRE_ACTION_LABELS = {
  extend: 'Extending this hire',
  shorten: 'Shortening this hire',
  change_start: "Changing this hire's start date",
};

/**
 * The sharpest applicable "this record is already closed" label for an item,
 * or `null` if the order is still open (submitted/rejected/cancelled — a
 * cancelled order cannot reach these PATCH routes at all, but is not
 * asserted here). `hire_status === 'off_hired'` wins over the order status:
 * an item already off-hired is the strongest, least-ambiguous case the
 * owner named.
 */
export function closedHireStatusLabel(item) {
  if (!item) return null;
  if (item.hire_status === 'off_hired') return 'off-hired';
  return CLOSED_ORDER_STATUS_LABELS[item.order_status] || null;
}

/** ConfirmDialog `title` for the money-warning gate. */
export function hireMoneyWarningTitle(item) {
  const closedLabel = closedHireStatusLabel(item);
  return closedLabel
    ? `Already ${closedLabel} — this still changes the price`
    : "This changes the order's price";
}

/** ConfirmDialog `message` for the money-warning gate. */
export function hireMoneyWarningMessage(action, item) {
  const actionLabel = HIRE_ACTION_LABELS[action] || "Changing this hire's dates";
  const closedLabel = closedHireStatusLabel(item);
  return closedLabel
    ? `${actionLabel} recalculates the order's line total and total price to match the new dates — even though the order has already been ${closedLabel}. It takes effect immediately.`
    : `${actionLabel} recalculates the order's line total and total price to match the new dates. It takes effect immediately.`;
}

// ─────────────────────────────────────────────────────────────────────────
// hireMoneyChangeSummary — the RICHER copy for the money dialog's body slot:
// current/new date + rate + price rows, plus a per-rate-type sentence and
// (extend / change-start only, weekly only) a free-days OFFER. Design spec:
// docs/superpowers/specs/2026-09-02-hire-extend-dialog-design.md §3.
//
// Rendered through `HireMoneyWarningDialog`'s `children` slot by BOTH
// surfaces — `HireActionModal.js` (extend) and `GanttView.js` (all three) —
// "two surfaces telling a buyer different things about the same hire" is the
// recorded failure class this file's own docblock already names.
//
// 🔑 NO NEW ARITHMETIC — every figure comes from `utils/hireCost.js`
// (`hireLineCost`, `hireDaysBetween`, `calendarDayGap`, `hireWeekBoundary`,
// `hireLineQuantity`, `hireRateDisplay`), formatted with the caller's
// `formatMoney` (VAT-aware, from `useVatMode()`) and `utils/dateFormat`'s
// `formatDate` — this module is a copy-and-wiring layer over that mirror,
// never a second implementation of it.
// ─────────────────────────────────────────────────────────────────────────
const ACTION_ROW_META = {
  extend: { field: 'hire_to', currentLabel: 'Current end date:', newLabel: 'New end date:' },
  shorten: { field: 'hire_to', currentLabel: 'Current end date:', newLabel: 'New end date:' },
  change_start: { field: 'hire_from', currentLabel: 'Current start date:', newLabel: 'New start date:' },
};

const pluralDays = (n) => `${n} day${n === 1 ? '' : 's'}`;
const pluralWeeks = (n) => `${n} week${n === 1 ? '' : 's'}`;

/**
 * `dateStr` shifted by `deltaDays` (may be negative), formatted `YYYY-MM-DD`.
 * `null` if `dateStr` is unparseable.
 *
 * 🪤 THIS IS NOT NEW HIRE-COST ARITHMETIC — it is the identical UTC
 * date-shift-and-format idiom `hireCost.js`'s own `hireWeekBoundary` already
 * uses to compute `fullWeekEndDate` (`parseHireDate` + `MS_PER_DAY`, then
 * `getUTCFullYear`/`getUTCMonth`/`getUTCDate`), applied in the OTHER
 * direction (subtracting, to find a target START date rather than an END
 * date). It is local to this file rather than exported from `hireCost.js`
 * because that file's mirror with the server is compared as TEXT
 * (`server/tests/regressions/hire-cost-client-parity.test.js`) and this
 * feature's brief is explicitly client-only — no server file changes. Adding
 * a new business rule to the mirrored range would need a server twin;
 * reusing its two already-exported primitives to shift a date needs none.
 */
function shiftDateStr(dateStr, deltaDays) {
  const ms = parseHireDate(dateStr);
  if (ms === null) return null;
  const shifted = new Date(ms + deltaDays * MS_PER_DAY);
  const y = shifted.getUTCFullYear();
  const m = String(shifted.getUTCMonth() + 1).padStart(2, '0');
  const d = String(shifted.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * Weekly-rate sentence + offer flag. `isExtendLike` decides which of the
 * offer/no-offer templates applies; `isChangeStart` only changes WORDING
 * ("start moved … earlier/later" vs "extended"/"shortened by").
 *
 * 🔑 **Why change-start's offer has NO computed target date, unlike
 * extend's.** `hireWeekBoundary(from, to)`'s `fullWeekEndDate` is always
 * relative to extending the END forward — it cannot express "move the START
 * back further", because that is the mirror direction. `shiftDateStr` (above)
 * supplies exactly that mirror for change-start, reusing the same two
 * primitives `hireWeekBoundary` itself uses internally.
 */
function weeklySentence({ isExtendLike, isChangeStart, n, boundary, oldCost, newCost, newFrom }) {
  const verb = isChangeStart
    ? `start moved ${pluralDays(n)} ${isExtendLike ? 'earlier' : 'later'}`
    : `${isExtendLike ? 'extended' : 'shortened'} by ${pluralDays(n)}`;

  if (!isExtendLike) {
    // shorten, or change-start moving the start LATER (fewer days used) —
    // ⚖️ the offer is NEVER shown here, ruled 2026-08-27, and a change-start
    // that reduces the span is economically a shorten wearing a different
    // action name.
    let sentence = `Weekly rate — ${verb}; ${pluralWeeks(boundary.billedWeeks)} are now charged.`;
    if (boundary.freeDays > 0) {
      sentence += ` The ${isChangeStart ? 'start' : 'end'} date is mid-week — the full week is still charged.`;
    }
    return { sentence, offer: false };
  }

  if (oldCost === newCost) {
    // §3.5 — the extension is entirely absorbed by a week already paid for.
    return { sentence: `Weekly rate — ${verb}, within the week already paid for. No extra cost.`, offer: true };
  }

  if (boundary.freeDays === 0) {
    return {
      sentence: `Weekly rate — ${verb}, now ${pluralWeeks(boundary.billedWeeks)} full. No part-week is charged.`,
      offer: false,
    };
  }

  // Mid-week landing — the OFFER, never a warning (owner ruling,
  // hire-weekly-rate-mid-week-prompt.md).
  const daysUsed = 7 - boundary.freeDays;
  const moveLabel = isChangeStart
    ? `move the start earlier by ${boundary.freeDays} more ${boundary.freeDays === 1 ? 'day' : 'days'}`
    : `extend by ${boundary.freeDays} more ${boundary.freeDays === 1 ? 'day' : 'days'}`;
  const targetDate = isChangeStart ? shiftDateStr(newFrom, -boundary.freeDays) : boundary.fullWeekEndDate;
  return {
    sentence: `Weekly rate — ${verb}. A full week is charged but only ${daysUsed} of its days are hired: you could ${moveLabel}, to ${formatDate(targetDate)}, for the same cost.`,
    offer: true,
  };
}

function dailySentence({ isExtendLike, isChangeStart, n, qty, item, formatMoney }) {
  const verb = isChangeStart
    ? `start moved ${pluralDays(n)} ${isExtendLike ? 'earlier' : 'later'}`
    : `${isExtendLike ? 'extended' : 'shortened'} by ${pluralDays(n)}`;
  const qtySuffix = qty > 1 ? ` × ${qty} items` : '';
  return { sentence: `Daily rate — ${verb} at ${formatMoney(item.daily_rate)} a day${qtySuffix}.`, offer: false };
}

function totalSentence({ isChangeStart, isExtendLike }) {
  const gerund = isChangeStart
    ? 'moving the start date'
    : isExtendLike
      ? 'extending the dates'
      : 'shortening the dates';
  return { sentence: `This item is on a fixed total rate — ${gerund} does not change the price.`, offer: false };
}

/**
 * The rows + sentence + offer for the money dialog's body slot.
 *
 * @param {'extend'|'shorten'|'change_start'} action
 * @param {object} item — the hire line item (hire_from/hire_to/hire_rate_type/
 *   daily_rate/weekly_rate/total_hire_rate/quantity).
 * @param {string} newDateStr — the staged `new_hire_to` (extend/shorten) or
 *   `new_hire_from` (change_start).
 * @param {(n: number) => string} formatMoney — VAT-aware formatter from
 *   `useVatMode()`. Module-level function, so the hook is not in scope here —
 *   callers must pass it in (same convention as `hireRateDisplay`).
 * @returns {{ rows: Array<{label:string,value:string,highlight?:boolean}>,
 *   sentence: string|null, offer: boolean } | null} `null` only when there is
 *   nothing to say at all (no item, or no staged date). When the SPAN is
 *   unknowable (`hireDaysBetween` returns null on either side), `rows` still
 *   carries the date/rate rows but `sentence` is `null` and no price rows are
 *   appended — §3.5's "omit, never show a false £0" rule.
 */
export function hireMoneyChangeSummary(action, item, newDateStr, formatMoney) {
  if (!item || !newDateStr) return null;
  const meta = ACTION_ROW_META[action];
  if (!meta) return null;

  const { field, currentLabel, newLabel } = meta;
  const oldDateStr = item[field];
  const qty = hireLineQuantity(item);
  const rateType = normaliseHireRateType(item.hire_rate_type);

  const rows = [
    { label: currentLabel, value: formatDate(oldDateStr) },
    { label: newLabel, value: formatDate(newDateStr), highlight: true },
    { label: 'Rate:', value: hireRateDisplay(item, formatMoney) + (qty > 1 ? ` × ${qty} items` : '') },
  ];

  // The NEW span, in (from, to) order regardless of which end is changing.
  const newFrom = field === 'hire_from' ? newDateStr : item.hire_from;
  const newTo = field === 'hire_from' ? item.hire_to : newDateStr;
  const newDays = hireDaysBetween(newFrom, newTo);
  // The OLD span too — `oldCost` reads it via `hireLineCost(item)`, and an
  // unknown OLD span must not be allowed to render as a false £0 either.
  const oldDays = hireDaysBetween(item.hire_from, item.hire_to);

  if (newDays == null || oldDays == null) {
    return { rows, sentence: null, offer: false };
  }

  const oldCost = hireLineCost(item);
  const newCost = hireLineCost({ ...item, [field]: newDateStr });
  rows.push({ label: 'Current price:', value: formatMoney(oldCost) });
  rows.push({ label: 'New price:', value: formatMoney(newCost) });

  const gap = calendarDayGap(oldDateStr, newDateStr);
  const n = gap == null ? 0 : Math.abs(gap);
  const isChangeStart = action === 'change_start';
  // ⚖️ Extend always adds days at the end; shorten always removes them.
  // Change-start can do either — moving the start EARLIER adds days (the
  // extend-shaped case, and the one the free-days offer applies to since
  // Pete's 2026-09-02 ruling); moving it LATER removes them (the
  // shorten-shaped case, where the offer never applies).
  const isExtendLike = action === 'extend' ? true : action === 'shorten' ? false : gap < 0;

  let result;
  if (rateType === 'total') {
    result = totalSentence({ isChangeStart, isExtendLike });
  } else if (rateType === 'daily') {
    result = dailySentence({ isExtendLike, isChangeStart, n, qty, item, formatMoney });
  } else {
    const boundary = hireWeekBoundary(newFrom, newTo);
    result = boundary
      ? weeklySentence({ isExtendLike, isChangeStart, n, boundary, oldCost, newCost, newFrom })
      : { sentence: null, offer: false };
  }

  return { rows, sentence: result.sentence, offer: result.offer };
}
